import { useState, useMemo } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { fmtEUR, fmtDate, INVOICE_STATUS } from "../lib/format";
import { Layers } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

export const MonthlyInvoiceDialog = ({ open, onOpenChange, clients, invoices, onSaved }) => {
  const [clientId, setClientId] = useState("");
  const [month, setMonth] = useState("");
  const [selected, setSelected] = useState({});
  const [saving, setSaving] = useState(false);
  const [withArchived, setWithArchived] = useState(false);

  const clientInvoices = useMemo(
    () => (clientId
      ? invoices.filter((i) => i.client_id === clientId && (withArchived || !i.combined_into) && (!month || (i.date_emission || "").startsWith(month)))
      : []),
    [clientId, month, invoices, withArchived]
  );
  const chosen = clientInvoices.filter((i) => selected[i.id]);
  const total = chosen.reduce((s, i) => s + Number(i.total_ttc || 0), 0);

  const toggle = (id) => setSelected((p) => ({ ...p, [id]: !p[id] }));
  const changeClient = (id) => { setClientId(id); setSelected({}); };

  const generate = async () => {
    if (!clientId) return toast.error("Sélectionnez une société / un client");
    if (!chosen.length) return toast.error("Sélectionnez au moins une facture");
    setSaving(true);
    try {
      const inv = await api.combineInvoices({ client_id: clientId, invoice_ids: chosen.map((i) => i.id) });
      toast.success(`Facture ${inv.number} générée (${chosen.length} regroupée(s))`);
      onOpenChange(false);
      setClientId("");
      setSelected({});
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de la génération");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { setClientId(""); setMonth(""); setSelected({}); } onOpenChange(v); }}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="monthly-invoice-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <Layers className="h-6 w-6 text-[#C5A059]" /> Regrouper des factures
          </DialogTitle>
          <DialogDescription>
            Sélectionnez une société puis cochez les factures enregistrées à regrouper. Une seule facture sera générée reprenant toutes leurs lignes.
          </DialogDescription>
        </DialogHeader>

        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label>Société / Client</Label>
            <Select value={clientId} onValueChange={changeClient}>
              <SelectTrigger data-testid="monthly-client-select"><SelectValue placeholder="Choisir une société" /></SelectTrigger>
              <SelectContent>
                {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Filtrer par mois (optionnel)</Label>
            <input type="month" value={month} onChange={(e) => { setMonth(e.target.value); setSelected({}); }} data-testid="monthly-month-filter" className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
          </div>
        </div>

        <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer" data-testid="combine-include-archived">
          <input type="checkbox" className="h-4 w-4 accent-[#C5A059]" checked={withArchived} onChange={(e) => { setWithArchived(e.target.checked); setSelected({}); }} data-testid="combine-include-archived-check" />
          Inclure les factures archivées (déjà regroupées)
        </label>

        <div className="rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-100 text-xs font-semibold uppercase tracking-wider text-slate-500">
            Factures enregistrées {clientId && `· ${chosen.length}/${clientInvoices.length} sélectionnée(s)`}
          </div>
          {!clientId ? (
            <p className="text-sm text-slate-400 py-8 text-center">Sélectionnez une société</p>
          ) : clientInvoices.length ? (
            <div className="divide-y divide-slate-50 max-h-72 overflow-y-auto">
              {clientInvoices.map((inv) => (
                <label key={inv.id} className="flex items-center gap-3 px-4 py-2.5 text-sm cursor-pointer hover:bg-slate-50" data-testid={`combine-invoice-${inv.number}`}>
                  <input type="checkbox" className="h-4 w-4 accent-[#C5A059]" checked={!!selected[inv.id]} onChange={() => toggle(inv.id)} data-testid={`combine-check-${inv.number}`} />
                  <span className="font-mono text-slate-700 w-32">{inv.number}</span>
                  <span className="text-slate-500 flex-1">{fmtDate(inv.date_emission)}{inv.combined_into && <span className="ml-2 text-[10px] text-slate-400">Archivée ↳ {inv.combined_into}</span>}</span>
                  <span className={`text-[11px] px-2 py-0.5 rounded-full border ${INVOICE_STATUS[inv.status]?.cls || ""}`}>{INVOICE_STATUS[inv.status]?.label}</span>
                  <span className="font-mono font-semibold text-slate-900 w-24 text-right">{fmtEUR(inv.total_ttc)}</span>
                </label>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-400 py-8 text-center">Aucune facture enregistrée pour cette société</p>
          )}
          {chosen.length > 0 && (
            <div className="flex items-center justify-between px-4 py-3 bg-slate-900 text-white">
              <span className="text-[#C5A059] font-semibold text-sm">Total TTC regroupé</span>
              <span className="font-mono text-lg font-bold text-[#C5A059]" data-testid="monthly-total">{fmtEUR(total)}</span>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button onClick={generate} disabled={saving || !chosen.length} className="bg-slate-900 hover:bg-slate-800" data-testid="generate-monthly-invoice-btn">
            {saving ? "Génération..." : "Générer la facture groupée"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
